import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Users, FileText, ClipboardList, Receipt, TrendingUp, Target, Calendar, Banknote, CreditCard, AlertTriangle, Bell } from "lucide-react";

const OverviewPage = ({ stats }) => {
  const { token } = useAuth();
  const [weeklySales, setWeeklySales] = useState([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const [inactiveData, setInactiveData] = useState({ total_inactive: 0, clients: [] });

  useEffect(() => {
    fetchWeeklySales();
    fetchInactiveClients();
  }, []);

  const fetchInactiveClients = async () => {
    try {
      const response = await axios.get(`${API}/clients/inactive`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInactiveData(response.data);
    } catch (error) {
      console.error("Error fetching inactive clients:", error);
    }
  };

  const fetchWeeklySales = async () => {
    try {
      const response = await axios.get(`${API}/stats/weekly-sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeeklySales(response.data);
    } catch (error) {
      console.error("Error fetching weekly sales:", error);
    } finally {
      setLoadingChart(false);
    }
  };

  const formatCurrency = (amount) =>
    `RD$ ${amount?.toLocaleString("es-DO", { minimumFractionDigits: 0 })}`;

  const statCards = [
    {
      label: "Total Clientes",
      value: stats?.total_clients || 0,
      icon: Users,
      color: "bg-blue-50 text-blue-600",
    },
    {
      label: "Pedidos",
      value: stats?.total_orders || 0,
      icon: ClipboardList,
      color: "bg-amber-50 text-amber-600",
    },
    {
      label: "Cotizaciones",
      value: stats?.total_quotes || 0,
      icon: FileText,
      color: "bg-purple-50 text-purple-600",
    },
    {
      label: "Facturas",
      value: stats?.total_invoices || 0,
      icon: Receipt,
      color: "bg-emerald-50 text-emerald-600",
    },
  ];

  // Calculate max value for chart scaling
  const maxSale = Math.max(...weeklySales.map((w) => w.total), 1);

  // Get progress bar color based on percentage
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "bg-emerald-500";
    if (percentage >= 75) return "bg-blue-500";
    if (percentage >= 50) return "bg-amber-500";
    return "bg-red-500";
  };

  return (
    <div className="animate-fade-in">
      {/* Welcome Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800 mb-1">
          Dashboard de Ventas
        </h2>
        <p className="text-slate-500">
          Resumen de actividad comercial - OHTSU Del Caribe
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="stat-card hover-lift"
            data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-value">{card.value}</p>
                <p className="stat-label">{card.label}</p>
              </div>
              <div className={`p-2 rounded ${card.color}`}>
                <card.icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Sales Progress Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Sales vs Goal */}
        <div className="bg-white rounded border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target size={20} className="text-blue-600" />
            <h3 className="font-semibold text-slate-800">Meta del Mes</h3>
          </div>

          {/* Sales and Collections Breakdown */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-blue-50 rounded">
              <div className="flex items-center gap-2 mb-1">
                <Receipt size={14} className="text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">Facturado</span>
              </div>
              <p className="text-lg font-bold text-blue-700 mono">
                {formatCurrency(stats?.monthly_sales || 0)}
              </p>
            </div>
            <div className="p-3 bg-emerald-50 rounded">
              <div className="flex items-center gap-2 mb-1">
                <Banknote size={14} className="text-emerald-600" />
                <span className="text-xs text-emerald-600 font-medium">Cobrado ({stats?.total_receipts_count || 0})</span>
              </div>
              <p className="text-lg font-bold text-emerald-700 mono">
                {formatCurrency(stats?.monthly_collections || 0)}
              </p>
            </div>
          </div>

          {/* Total vs Goal */}
          <div className="flex items-end justify-between mb-2">
            <div>
              <p className="text-xs text-slate-500">Total del Mes</p>
              <p className="text-2xl font-bold text-slate-800 mono">
                {formatCurrency(stats?.monthly_total || 0)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Meta</p>
              <p className="text-lg font-semibold text-slate-600 mono">
                {formatCurrency(stats?.monthly_goal || 500000)}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative mb-4">
            <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${getProgressColor(stats?.goal_percentage || 0)} transition-all duration-500`}
                style={{ width: `${Math.min(stats?.goal_percentage || 0, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-xs text-slate-500">0%</span>
              <span
                className={`text-sm font-bold ${(stats?.goal_percentage || 0) >= 100
                    ? "text-emerald-600"
                    : (stats?.goal_percentage || 0) >= 75
                      ? "text-blue-600"
                      : "text-amber-600"
                  }`}
              >
                {stats?.goal_percentage || 0}% completado
              </span>
              <span className="text-xs text-slate-500">100%</span>
            </div>
          </div>

          {/* Status Message */}
          <div
            className={`p-3 rounded ${(stats?.goal_percentage || 0) >= 100
                ? "bg-emerald-50 text-emerald-700"
                : (stats?.goal_percentage || 0) >= 75
                  ? "bg-blue-50 text-blue-700"
                  : "bg-amber-50 text-amber-700"
              }`}
          >
            <p className="text-sm font-medium">
              {(stats?.goal_percentage || 0) >= 100
                ? "🎉 ¡Meta alcanzada! Excelente trabajo."
                : (stats?.goal_percentage || 0) >= 75
                  ? "📈 Muy cerca de la meta. ¡Sigue así!"
                  : `💪 Faltan ${formatCurrency((stats?.monthly_goal || 500000) - (stats?.monthly_total || 0))} para alcanzar la meta`}
            </p>
          </div>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded p-5 text-white">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} />
            <h3 className="font-semibold">Balance Pendiente Total</h3>
          </div>
          <p className="text-4xl font-bold mono mb-2" data-testid="pending-balance">
            {formatCurrency(stats?.pending_balance || 0)}
          </p>
          <p className="text-slate-300 text-sm">
            Suma de todos los balances de clientes
          </p>

          <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 gap-4">
            <div>
              <p className="text-slate-400 text-xs">Facturas Emitidas</p>
              <p className="text-xl font-bold">{stats?.total_invoices || 0}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Clientes Activos</p>
              <p className="text-xl font-bold">{stats?.total_clients || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Sales Chart */}
      <div className="bg-white rounded border border-slate-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-slate-600" />
            <h3 className="font-semibold text-slate-800">Ventas Últimos 3 Meses</h3>
          </div>
          <span className="text-xs text-slate-500">Agrupado por semana</span>
        </div>

        {loadingChart ? (
          <div className="h-48 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : weeklySales.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-slate-500">
            No hay datos de ventas disponibles
          </div>
        ) : (
          <div className="relative">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-8 w-16 flex flex-col justify-between text-xs text-slate-500 mono">
              <span>{formatCurrency(maxSale).replace("RD$ ", "")}</span>
              <span>{formatCurrency(maxSale / 2).replace("RD$ ", "")}</span>
              <span>0</span>
            </div>

            {/* Chart */}
            <div className="ml-16 overflow-x-auto">
              <div className="flex items-end gap-2 h-48 min-w-max pb-8">
                {weeklySales.map((week, index) => {
                  const height = maxSale > 0 ? (week.total / maxSale) * 100 : 0;
                  return (
                    <div
                      key={week.week_start}
                      className="flex flex-col items-center group"
                      style={{ minWidth: "50px" }}
                    >
                      {/* Tooltip */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute -top-12 bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                        <p className="font-semibold">{formatCurrency(week.total)}</p>
                        <p className="text-slate-300">{week.count} facturas</p>
                      </div>

                      {/* Bar */}
                      <div
                        className={`w-8 rounded-t transition-all duration-300 hover:opacity-80 ${index === weeklySales.length - 1
                            ? "bg-blue-500"
                            : week.total > 0
                              ? "bg-blue-300"
                              : "bg-slate-200"
                          }`}
                        style={{
                          height: `${Math.max(height, 2)}%`,
                          minHeight: "4px",
                        }}
                      />

                      {/* Label */}
                      <span className="text-xs text-slate-500 mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                        {week.week_label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500"></div>
                <span>Semana actual</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-300"></div>
                <span>Semanas anteriores</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Inactive Clients Alert */}
      {inactiveData.total_inactive > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-full">
                <Bell size={20} className="text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  Clientes Requieren Seguimiento
                </h4>
                <p className="text-sm text-amber-700 mt-1">
                  {inactiveData.total_inactive} cliente(s) no han realizado pedidos en los últimos 30 días.
                  {inactiveData.clients.filter(c => c.priority === "high").length > 0 && (
                    <span className="font-semibold text-red-600 ml-1">
                      ({inactiveData.clients.filter(c => c.priority === "high").length} urgentes)
                    </span>
                  )}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {inactiveData.clients.slice(0, 3).map((client) => (
                    <span
                      key={client.id}
                      className={`px-2 py-1 rounded text-xs font-medium ${client.priority === "high"
                          ? "bg-red-100 text-red-700"
                          : "bg-amber-100 text-amber-700"
                        }`}
                    >
                      {client.name} ({client.days_inactive}d)
                    </span>
                  ))}
                  {inactiveData.clients.length > 3 && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600">
                      +{inactiveData.clients.length - 3} más
                    </span>
                  )}
                </div>
              </div>
            </div>
            <a
              href="/inactive-clients"
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded transition-colors"
            >
              Ver Lista
            </a>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Acciones Rápidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href="/clients"
            className="flex items-center gap-3 p-4 border border-slate-200 rounded hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            data-testid="quick-action-clients"
          >
            <Users className="text-blue-600" size={24} />
            <div>
              <p className="font-medium text-slate-800">Ver Clientes</p>
              <p className="text-sm text-slate-500">Gestiona tu cartera</p>
            </div>
          </a>
          <a
            href="/articles"
            className="flex items-center gap-3 p-4 border border-slate-200 rounded hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            data-testid="quick-action-articles"
          >
            <ClipboardList className="text-amber-600" size={24} />
            <div>
              <p className="font-medium text-slate-800">Ver Artículos</p>
              <p className="text-sm text-slate-500">Catálogo de productos</p>
            </div>
          </a>
          <a
            href="/document/new"
            className="flex items-center gap-3 p-4 border border-slate-200 rounded hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            data-testid="quick-action-document"
          >
            <FileText className="text-purple-600" size={24} />
            <div>
              <p className="font-medium text-slate-800">Nuevo Documento</p>
              <p className="text-sm text-slate-500">Crear pedido o cotización</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
